/**
 * The spell sheet, fetched once per table.
 *
 * Over HTTP rather than on the snapshot for the same reason the places list
 * is: it is **per viewer**. Which spells a character is holding is exactly the
 * information the other side of a fight would most like to have, so it can
 * never ride on a room-wide socket event.
 *
 * One fetch, two consumers — the Spells panel and the combat declaration
 * window both need the loadout, and two fetches would be two answers that
 * could disagree for as long as a round.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.js';

export type CasterKind = 'wizard' | 'priest';

export interface MemorisedSpell {
  readonly name: string;
  readonly level: number;
  readonly casterKind: CasterKind;
  readonly expended: boolean;
  /**
   * What was chosen WHEN MEMORISING: Conjure Elemental's element, Fire Shield's
   * warm or chill. Stored on the slot, so the held list can say which it is
   * rather than leave the caster to remember.
   */
  readonly elementType?: string;
  /**
   * The rare spell that offers a real caster-chosen mode at the moment of
   * casting (Monster Summoning VII: several weaker allies now, or one
   * stronger ally two rounds from now) — server-derived, never stored.
   * Absent for every ordinary spell.
   */
  readonly modes?: readonly { readonly id: string; readonly label: string }[];
  /**
   * The printed reverse's own name — Bless's own "Curse" — server-derived,
   * never stored. Absent for every spell that prints none.
   */
  readonly reverseName?: string;
  /** Animal Summoning: how many different kinds the caster may name. Absent for every other spell. */
  readonly callsAnimals?: { readonly attempts: number };
  /** Whether this spell offers "optionally, a single item can be blessed" — Bless's own item-enchantment clause. */
  readonly canBlessItem?: boolean;
}

export interface OfferedSpell {
  readonly name: string;
  readonly level: number;
  readonly casterKind: CasterKind;
  readonly school?: string;
  readonly sphere?: string;
  readonly castingTime: string;
  readonly range: string;
  readonly duration: string;
  readonly savingThrow: string;
  /**
   * Conjure Elemental's own printed choice, made at memorisation. The
   * "Study / pray" picker needs to know this BEFORE the player submits.
   * Absent for every other spell.
   */
  readonly elementTypes?: readonly string[];
  /**
   * True when the engine works this spell's effect out (damage, a save, a
   * duration, a condition on the target). Absent means the DM narrates it — the
   * cast still spends the slot and takes its casting time, but nothing is
   * resolved by dice. Said on the list so a caster is not left guessing which.
   */
  readonly resolved?: boolean;
}

export interface SpellSheet {
  readonly casts: boolean;
  readonly profiles: readonly {
    readonly characterClass: string;
    readonly casterKind: CasterKind;
    readonly classLevel: number;
    readonly casterLevel: number;
    readonly slots: readonly number[];
  }[];
  readonly memorised: readonly MemorisedSpell[];
  readonly spellbook: readonly { readonly name: string; readonly level: number; readonly pages?: number }[];
  /** The wizard's book and each scroll in the pack that could be copied into it; server-derived. */
  readonly copying?: {
    readonly bookType: string;
    /** How many books the wizard owns (1 + blank ones bought). */
    readonly books: number;
    /** Pages across all of them. */
    readonly capacity: number;
    /** What another blank book of this kind costs, spoken by the server. */
    readonly blankBookCost: string;
    readonly pagesUsed: number;
    /** Spells whose pages have not been measured yet (written before pages were tracked). */
    readonly unmeasured: number;
    readonly costPerPage: string;
    readonly scrolls: readonly {
      readonly item: string;
      readonly spell: string;
      readonly level: number;
      readonly pages?: number;
      readonly cost?: string;
      readonly maxCost: string;
      readonly barred?: string;
    }[];
    /** Spells in a fellow wizard's book that may be copied: the same player's own, or lent. */
    readonly sources?: readonly {
      readonly characterId: string;
      readonly name: string;
      readonly spell: string;
      readonly level: number;
      readonly via: 'yours' | 'lent';
    }[];
    /** Wizards at the table a spell may be lent to. */
    readonly wizards?: readonly { readonly characterId: string; readonly name: string }[];
    /** What this wizard has lent and not yet had copied. */
    readonly lent?: readonly { readonly spell: string; readonly toCharacterId: string; readonly toName: string }[];
  };
  /** Free slots per list, index 0 = spell level 1. Server-computed. */
  readonly free: Partial<Record<CasterKind, readonly number[]>>;
  /** Everything this character could legally memorise, already filtered. */
  readonly available: readonly OfferedSpell[];
  /**
   * Free spells earned at level-up and not yet chosen, and what they may be.
   *
   * Both server-side: "a level they can now cast" is a read across the slot
   * tables with two Intelligence gates on top, and a client that filtered the
   * 312 itself would be re-deciding the rule.
   */
  readonly toLearn?: number;
  readonly learnable?: readonly OfferedSpell[];
}

const EMPTY: SpellSheet = {
  casts: false,
  profiles: [],
  memorised: [],
  spellbook: [],
  free: {},
  available: [],
};

/**
 * `token` is anything that changes when the server may have rewritten the
 * loadout — a `character:update`, or a round resolving. Without it a slot spent
 * in combat would keep showing as available until a reload.
 */
export function useSpellSheet(
  campaignId: string | undefined,
  token?: unknown,
  /**
   * Whose sheet. Absent is the caller's own, which is every ordinary use.
   *
   * A **retainer's** id is the other case: a henchman has no login, so their
   * loadout reaches no screen unless their patron asks for it — and a combat
   * panel offering the patron's own spells while declaring a cast for the
   * henchman names spells they do not hold.
   */
  characterId?: string,
): { sheet: SpellSheet; reload: () => void } {
  const [sheet, setSheet] = useState<SpellSheet>(EMPTY);

  const reload = useCallback(() => {
    if (!campaignId) return;
    const query = characterId ? `?characterId=${encodeURIComponent(characterId)}` : '';
    void api
      .get<SpellSheet>(`/api/campaigns/${campaignId}/spells${query}`)
      // A character who casts nothing answers `casts: false`, not an error —
      // but a genuinely failed request must not leave a stale loadout on
      // screen either, so both land on the empty sheet.
      .then((body) => setSheet(body ?? EMPTY))
      .catch(() => setSheet(EMPTY));
  }, [campaignId, characterId]);

  useEffect(reload, [reload, token]);

  return { sheet, reload };
}

/** The slots that are still there to spend. */
export function unspent(sheet: SpellSheet): readonly MemorisedSpell[] {
  return sheet.memorised.filter((entry) => !entry.expended);
}

/** What a copy attempt came to, as the server reports it. */
export interface CopyResult {
  readonly outcome: 'learned' | 'failed';
  readonly spell: string;
  readonly pages: number;
  readonly roll: number;
  readonly needed: number;
}

/** The sentence a copy attempt earns — the working is shown so the table can check it. */
export function describeCopyResult(result: CopyResult): string {
  return result.outcome === 'learned'
    ? `Copied ${result.spell} into the book (${result.pages} pages) — ${result.roll} against ${result.needed}%.`
    : `${result.spell} would not take — ${result.roll} against ${result.needed}%. The scroll and the components are gone, and it cannot be tried again until the next level.`;
}
